import React from "react";
import { IeBenefitType } from "../../../types/entities";
import BenefitDetail from "../benefits/BenefitDetail";
import { PictureWithSources } from "../../common/PictureWithSources";
import styled from "styled-components";

const Block = styled.div`
    margin: 3rem 0;
`;

const Icon = styled(PictureWithSources)`
    display: inline-block;
    width: 100%;
    height: 100%;
    margin-right: 0.5rem;
`;

const BlockTitle = styled.h3`
    align-items: baseline;
    display: grid;
    font-family: var(--ff-title);
    font-size: 2.25rem;
    grid-template-columns: auto 4rem;
    justify-content: space-between;
    margin-bottom: 1rem;
    text-transform: uppercase;
`;

const BalanceHeader = styled.span`
    font-family: var(--ff-text);
    font-size: 0.4em;
    font-weight: 700;
    text-align: center;
    text-transform: none;
`;

const List = styled.ul`
    list-style-type: none;
    margin-bottom: 0;
    padding: 0;

    & > li {
        align-items: center;
        cursor: pointer;
        display: grid;
        gap: 1rem;
        grid-template-columns: 3rem auto 4rem;
        font-size: 0.9em;
        margin-bottom: 1.5rem;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const ItemText = styled.span`
    display: flex;
    flex-direction: column;
`;

const ItemName = styled.span`
    font-size: 1.1rem;
    font-weight: 700;
`;

const Alert = styled.span`
    background: var(--primary);
    color: white;
    font-size: 0.8em;
    padding: 0.25em 0.75em;
`;

const Balance = styled.span`
align-items: center;
    background: #F0F0F0;
    display: flex;
    font-family: var(--ff-title);
    font-size: 1.5rem;
    height: 2.5rem;
    justify-content: center;
    line-height: 1em;
    margin: 0 auto;
    text-align: center;
    width: 2.5rem;
`;

interface Props {
    data: {
        program: IeBenefitType[],
        achievements: IeBenefitType[]
    }
};

const BenefitsList: React.FC<Props> = ({ data: { program, achievements } }) => {
    const [selectedBenefit, setSelectedBenefit] = React.useState<IeBenefitType | null>(null);

    if (!program.length && !achievements.length) {
        return null;
    }

    return (
        <>
            <BenefitsBlock benefits={achievements} onSelect={setSelectedBenefit} title="Tus beneficios obtenidos" displayBalance />
            <BenefitsBlock benefits={program} onSelect={setSelectedBenefit} title="Beneficios del programa" />
            <BenefitDetail
                benefit={selectedBenefit}
                visible={selectedBenefit !== null}
                onClose={() => setSelectedBenefit(null)}
            />
        </>
    );
};

interface BlockProps {
    benefits: IeBenefitType[],
    displayBalance?: boolean,
    onSelect: (benefit: IeBenefitType) => void,
    title: string
}

const BenefitsBlock: React.FC<BlockProps> = ({ benefits, displayBalance, onSelect, title }) => {
    if (!benefits.length) {
        return null;
    }

    return (
        <Block>
            <BlockTitle>
                <span>{title}</span>
                {displayBalance && <BalanceHeader>Cantidad</BalanceHeader>}
            </BlockTitle>
            <List>
                {benefits.map((benefit, index) => (
                    <li key={index} onClick={() => onSelect(benefit)}>
                        <Icon images={benefit.images.icon} />
                        <ItemText>
                            <ItemName>
                                {benefit.title}
                            </ItemName>
                            {benefit.alert && (
                                <span>
                                    <Alert>
                                        {benefit.alert}
                                    </Alert>
                                </span>
                            )}
                        </ItemText>
                        {displayBalance && (
                            <Balance>
                                {getBalanceDisplay(benefit)}
                            </Balance>
                        )}
                    </li>
                ))}
            </List>
        </Block>
    );
}

const getBalanceDisplay = (benefit: IeBenefitType): JSX.Element => {
    const balance = benefit.balance;
    const hasBalanceByCinema = benefit.cinemas && benefit.cinemas.length > 0;

    if (!benefit.display_balance || hasBalanceByCinema) {
        return PlusSign;
    }
    
    if (!balance) {
        return <>0</>;
    }

    if (balance >= 10) {
        return <>9{PlusSign}</>;
    }
    
    return <>{balance.toString()}</>;
};

const PlusSign = <em className="fas fa-plus" style={{fontSize: '0.5em', verticalAlign: 'middle'}}></em>;

export default BenefitsList;